import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Download,
  RefreshCw,
  Search,
  XCircle,
  Eye,
} from "lucide-react";
import API from "../services/api";
import AdminLayout from "./AdminLayout";

const TABS = [
  { key: "failed", label: "Failed Payments" },
  { key: "visits", label: "Deposit Page Visits" },
];

function AdminDeposit() {
  const [tab, setTab] = useState("failed");
  const [failed, setFailed] = useState([]);
  const [visits, setVisits] = useState([]);
  const [visitSummary, setVisitSummary] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const loadFailed = async () => {
    try {
      const res = await API.get("/admin/failed-deposits");
      setFailed(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const loadVisits = async (date) => {
    try {
      const res = await API.get("/admin/payment-intents", { params: { date } });
      setVisits(res.data?.rows || []);
      setVisitSummary(res.data?.summary || []);
    } catch (err) {
      console.log(err);
    }
  };

  const refresh = async () => {
    setLoading(true);
    await Promise.all([loadFailed(), loadVisits(selectedDate)]);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => { loadVisits(selectedDate); }, [selectedDate]);

  const filteredFailed = useMemo(() => {
    const search = query.trim().toLowerCase();
    return failed.filter((r) => {
      const matchDate = !selectedDate || (r.created_at || "").slice(0, 10) === selectedDate;
      const matchSearch = !search || [r.id, r.user_name, r.user_phone, r.amount, r.remark]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(search));
      return matchDate && matchSearch;
    });
  }, [failed, query, selectedDate]);

  const filteredVisits = useMemo(() => {
    const search = query.trim().toLowerCase();
    return visits.filter((r) => {
      const matchSearch = !search || [r.id, r.user_name, r.user_phone, r.amount, r.event_type]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(search));
      return matchSearch;
    });
  }, [visits, query]);

  const exportFailed = () => {
    downloadCsv("failed-payments.csv", [
      ["ID", "Date", "User", "Mobile", "Amount", "Remark"],
      ...filteredFailed.map((r) => [r.id, fmt(r.created_at), r.user_name, r.user_phone, r.amount, r.remark])
    ]);
  };

  const exportVisits = () => {
    downloadCsv("deposit-visits.csv", [
      ["ID", "Date", "User", "Mobile", "Amount", "Event"],
      ...filteredVisits.map((r) => [r.id, fmt(r.created_at), r.user_name, r.user_phone, r.amount, r.event_type])
    ]);
  };

  const totalFailedAmt = filteredFailed.reduce((s, r) => s + Number(r.amount || 0), 0);
  const uniqueFailedUsers = new Set(filteredFailed.map((r) => r.user_id)).size;

  return (
    <AdminLayout>
      <section style={page}>
        <header style={pageHeader}>
          <div>
            <h1 style={title}>Failed Deposits</h1>
            <p style={subtitle}>Users who tried to deposit but payment failed or was rejected</p>
          </div>
          <div style={headerActions}>
            <button type="button" style={iconBtn} onClick={refresh} disabled={loading}>
              <RefreshCw size={14} style={loading ? { animation: "spin .8s linear infinite" } : {}} />
            </button>
            <button type="button" style={exportBtn} onClick={tab === "failed" ? exportFailed : exportVisits}>
              <Download size={14} /> Export
            </button>
          </div>
        </header>

        {/* Summary cards for failed tab */}
        {tab === "failed" && (
          <section style={summaryPanel}>
            <div style={summaryGrid}>
              <StatCard label="Failed Today" value={filteredFailed.length} color="#fca5a5" bg="rgba(239,68,68,0.2)" />
              <StatCard label="Amount Attempted" value={fmtMoney(totalFailedAmt)} color="#fde68a" bg="rgba(245,158,11,0.2)" />
              <StatCard label="Unique Users" value={uniqueFailedUsers} color="#c7d2fe" bg="rgba(99,102,241,0.2)" />
              <StatCard label="All Time Failed" value={failed.length} color="#f9a8d4" bg="rgba(236,72,153,0.15)" />
            </div>
          </section>
        )}

        {/* Summary cards for visits tab */}
        {tab === "visits" && visitSummary.length > 0 && (
          <section style={summaryPanel}>
            <div style={summaryGrid}>
              {visitSummary.map((s) => (
                <StatCard
                  key={s.event_type}
                  label={eventLabel(s.event_type)}
                  value={s.count}
                  color="#6ee7b7"
                  bg="rgba(16,185,129,0.15)"
                  sub={`${s.users} users`}
                />
              ))}
            </div>
          </section>
        )}

        {/* Tabs */}
        <div style={tabRow}>
          {TABS.map((t) => (
            <button key={t.key} type="button" style={tab === t.key ? activeTab : inactiveTab} onClick={() => setTab(t.key)}>
              {t.label}
              {t.key === "failed" && failed.length > 0 && (
                <span style={countBadge}>{failed.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div style={toolbar}>
          <div style={searchBox}>
            <Search size={14} color="#94a3b8" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search user, mobile..." style={searchInput} />
          </div>
          <label style={datePicker}>
            <CalendarDays size={13} />
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={dateInput} />
          </label>
          {selectedDate && (
            <button type="button" style={clearBtn} onClick={() => setSelectedDate("")}>All dates</button>
          )}
        </div>

        {/* Failed Payments Table */}
        {tab === "failed" && (
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>ID / Date</th>
                  <th style={th}>User</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Gateway Remark</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredFailed.map((r) => (
                  <tr key={r.id} style={tr}>
                    <td style={td}>
                      <strong>#{r.id}</strong>
                      <small style={small}>{fmt(r.created_at)}</small>
                    </td>
                    <td style={td}>
                      <div style={userCell}>
                        <span style={avatar}>{initial(r.user_name)}</span>
                        <div>
                          <strong style={userName}>{r.user_name}</strong>
                          <div style={userMeta}>{r.user_phone}</div>
                        </div>
                      </div>
                    </td>
                    <td style={td}><strong>{fmtMoney(r.amount)}</strong></td>
                    <td style={td}><span style={remarkTxt}>{r.remark || "-"}</span></td>
                    <td style={td}>
                      <span style={failBadge}>
                        <XCircle size={12} /> Failed
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredFailed.length === 0 && (
                  <tr><td style={emptyCell} colSpan="5">No failed payments found</td></tr>
                )}
              </tbody>
            </table>
            <footer style={tableFooter}>{filteredFailed.length} records</footer>
          </div>
        )}

        {/* Deposit Page Visits Table */}
        {tab === "visits" && (
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>ID / Date</th>
                  <th style={th}>User</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Event</th>
                  <th style={th}>Deposits Today</th>
                </tr>
              </thead>
              <tbody>
                {filteredVisits.map((r) => (
                  <tr key={r.id} style={tr}>
                    <td style={td}>
                      <strong>#{r.id}</strong>
                      <small style={small}>{fmt(r.created_at)}</small>
                    </td>
                    <td style={td}>
                      <div style={userCell}>
                        <span style={avatar}>{initial(r.user_name)}</span>
                        <div>
                          <strong style={userName}>{r.user_name}</strong>
                          <div style={userMeta}>{r.user_phone}</div>
                        </div>
                      </div>
                    </td>
                    <td style={td}>{r.amount ? fmtMoney(r.amount) : <span style={{ color: "#94a3b8" }}>—</span>}</td>
                    <td style={td}><EventBadge type={r.event_type} /></td>
                    <td style={td}>
                      <span style={Number(r.deposit_requests_today) > 0 ? successBadge : grayBadge}>
                        {r.deposit_requests_today || 0} deposit{Number(r.deposit_requests_today) !== 1 ? "s" : ""}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredVisits.length === 0 && (
                  <tr><td style={emptyCell} colSpan="5">No visits found for this date</td></tr>
                )}
              </tbody>
            </table>
            <footer style={tableFooter}>{filteredVisits.length} records</footer>
          </div>
        )}
      </section>
      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </AdminLayout>
  );
}

function StatCard({ label, value, color, bg, sub }) {
  return (
    <div style={{ ...summaryCard, background: bg, borderColor: color }}>
      <span style={{ color, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>{label}</span>
      <strong style={{ color: "#fff", fontSize: 22 }}>{value}</strong>
      {sub && <small style={{ color, fontSize: 11 }}>{sub}</small>}
    </div>
  );
}

function EventBadge({ type }) {
  const map = {
    visit:           { bg: "#dbeafe", color: "#1d4ed8" },
    proof_submitted: { bg: "#dcfce7", color: "#16a34a" },
    initiated:       { bg: "#fef3c7", color: "#d97706" },
  };
  const s = map[type] || { bg: "#f1f5f9", color: "#475569" };
  return <span style={{ background: s.bg, color: s.color, borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 }}>{eventLabel(type)}</span>;
}

function eventLabel(type) {
  const labels = { visit: "Page Visit", proof_submitted: "Proof Submitted", initiated: "Initiated" };
  return labels[type] || type;
}

function fmt(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function fmtMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function initial(name) {
  return (name || "U").trim().charAt(0).toUpperCase();
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const page        = { color: "#111827" };
const pageHeader  = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 };
const title       = { margin: 0, fontSize: 22, fontWeight: 800, color: "#1e1b4b" };
const subtitle    = { margin: "4px 0 0", color: "#64748b", fontSize: 13 };
const headerActions = { display: "flex", gap: 6 };
const iconBtn     = { width: 32, height: 32, display: "grid", placeItems: "center", border: "1px solid #e5e7eb", background: "#fff", color: "#6366f1", cursor: "pointer", borderRadius: 8 };
const exportBtn   = { height: 32, padding: "0 16px", display: "flex", alignItems: "center", gap: 7, border: 0, background: "#22c55e", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", borderRadius: 8 };
const summaryPanel = { background: "linear-gradient(135deg,#1e1b4b,#312e81)", borderRadius: 16, padding: 16, marginBottom: 16 };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 10 };
const summaryCard = { border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4 };
const tabRow      = { display: "flex", gap: 6, marginBottom: 12 };
const baseTab     = { height: 36, padding: "0 18px", border: "1.5px solid", borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 7 };
const activeTab   = { ...baseTab, background: "#1e1b4b", color: "#fff", borderColor: "#1e1b4b" };
const inactiveTab = { ...baseTab, background: "#fff", color: "#64748b", borderColor: "#e5e7eb" };
const countBadge  = { background: "#ef4444", color: "#fff", borderRadius: 999, padding: "1px 6px", fontSize: 10, fontWeight: 800 };
const toolbar     = { display: "flex", gap: 6, marginBottom: 12, alignItems: "center" };
const searchBox   = { height: 38, display: "flex", alignItems: "center", gap: 8, border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 12, padding: "0 12px", flex: 1, maxWidth: 320 };
const searchInput = { border: 0, outline: "none", flex: 1, fontSize: 13 };
const datePicker  = { height: 38, display: "flex", alignItems: "center", gap: 6, border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 12, padding: "0 12px", color: "#64748b", fontSize: 12, cursor: "pointer" };
const dateInput   = { border: 0, outline: "none", fontSize: 12, color: "#0f172a", cursor: "pointer" };
const clearBtn    = { height: 38, padding: "0 12px", border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 12, fontSize: 12, cursor: "pointer", color: "#64748b" };
const tableWrap   = { background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14, overflow: "hidden" };
const table       = { width: "100%", borderCollapse: "collapse" };
const th          = { padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" };
const tr          = { borderBottom: "1px solid #f1f5f9" };
const td          = { padding: "12px 14px", fontSize: 13, color: "#334155", verticalAlign: "top" };
const small       = { display: "block", fontSize: 11, color: "#94a3b8", marginTop: 2 };
const userCell    = { display: "flex", alignItems: "flex-start", gap: 10 };
const avatar      = { width: 30, height: 30, borderRadius: "50%", background: "#f1f5f9", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800, color: "#475569", flexShrink: 0 };
const userName    = { fontSize: 13, display: "block", color: "#0f172a" };
const userMeta    = { fontSize: 11, color: "#94a3b8", marginTop: 2 };
const failBadge   = { display: "inline-flex", alignItems: "center", gap: 4, background: "#fee2e2", color: "#dc2626", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 };
const successBadge = { background: "#dcfce7", color: "#16a34a", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 };
const grayBadge   = { background: "#f1f5f9", color: "#64748b", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 };
const remarkTxt   = { fontSize: 12, color: "#64748b" };
const emptyCell   = { padding: "40px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 };
const tableFooter = { padding: "10px 16px", borderTop: "1px solid #e2e8f0", fontSize: 12, color: "#94a3b8" };

export default AdminDeposit;
